import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveWorkout } from '@/contexts/ActiveWorkoutContext';
import { analyticsApi } from '@/lib/api';
import { formatWork } from '@/lib/utils';
import type { MuscleGroup } from '@/types';
import { MUSCLE_GROUP_LABELS, MUSCLE_GROUPS } from '@/types';
import { PlusIcon, ChevronRightIcon } from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';

const TARGET_SETS_PER_WEEK = 10; // Recommended sets per muscle group per week

// Circular progress ring component
function MuscleRing({ 
  muscle, 
  sets, 
  lastWeekSets 
}: { 
  muscle: MuscleGroup; 
  sets: number; 
  lastWeekSets: number;
}) {
  // CRITICAL: Ensure sets is never negative - this is the absolute count for current week
  const safeSets = Math.max(0, Math.round(sets) || 0);
  const safeLastWeekSets = Math.max(0, Math.round(lastWeekSets) || 0);
  
  const percentage = Math.min(100, (safeSets / TARGET_SETS_PER_WEEK) * 100);
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  // Color based on completion
  const getColor = () => {
    if (safeSets === 0) return { ring: '#e5e7eb', text: 'text-gray-400' };
    if (percentage >= 100) return { ring: '#22c55e', text: 'text-green-600' };
    if (percentage >= 50) return { ring: '#0ea5e9', text: 'text-primary-600' };
    return { ring: '#f59e0b', text: 'text-amber-500' };
  };
  
  const colors = getColor();
  // Only show positive difference (improvement), hide negative differences
  const diff = safeLastWeekSets > 0 && safeSets > safeLastWeekSets ? safeSets - safeLastWeekSets : 0;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-16">
        <svg className="w-full h-full transform -rotate-90">
          {/* Background ring */}
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="#f3f4f6"
            strokeWidth="6"
          />
          {/* Progress ring */}
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke={colors.ring}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-500"
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-lg font-bold ${colors.text}`}>{safeSets}</span>
        </div>
      </div>
      <p className="text-xs text-gray-600 mt-1 text-center font-medium">
        {MUSCLE_GROUP_LABELS[muscle]}
      </p>
      {diff !== 0 && (
        <p className={`text-xs ${diff > 0 ? 'text-green-600' : 'text-red-500'}`}>
          {diff > 0 ? '+' : ''}{diff}
        </p>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { activeWorkout } = useActiveWorkout();
  
  const { data: weeklyData, isLoading } = useQuery({
    queryKey: ['weeklyLive'],
    queryFn: async () => {
      const res = await analyticsApi.weeklyLive({ weeks: 2 });
      if (!res.success || !res.data) return [];
      
      // Ensure all values are non-negative - this is the absolute count for current week
      return res.data.map(week => ({
        ...week,
        total_work: Math.max(0, week.total_work || 0),
        total_sets: Math.max(0, week.total_sets || 0),
        workouts_count: Math.max(0, week.workouts_count || 0),
        by_muscle: Object.fromEntries(
          Object.entries(week.by_muscle || {}).map(([k, v]) => [k, Math.max(0, v as number)])
        ),
        sets_by_muscle: Object.fromEntries(
          Object.entries(week.sets_by_muscle || {}).map(([k, v]) => [k, Math.max(0, v as number)])
        ),
      }));
    },
  });

  const currentWeek = weeklyData?.[0];
  const lastWeek = weeklyData?.[1];

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Привет,</p>
            <p className="font-semibold text-gray-900">
              {user?.display_name || user?.login || 'Пользователь'}
            </p>
          </div>
          <Link
            to="/profile"
            className="flex-shrink-0"
          >
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
              {user?.avatar_base64 ? (
                <img
                  src={user.avatar_base64}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-medium text-primary-700">
                  {(user?.display_name || user?.login || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </Link>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Quick Action - Start or Continue workout */}
        {activeWorkout ? (
          <Link
            to={`/workouts/${activeWorkout.id}`}
            className="block w-full bg-primary-600 text-white rounded-xl p-4 shadow-lg hover:bg-primary-700 transition-colors touch-manipulation"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Pulsing indicator */}
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                </span>
                <div>
                  <p className="text-primary-100 text-sm">Тренировка в процессе</p>
                  <p className="text-lg font-semibold">Продолжить</p>
                </div>
              </div>
              <ChevronRightIcon className="w-6 h-6" />
            </div>
          </Link>
        ) : (
          <Link
            to="/workouts/new"
            className="block w-full bg-primary-600 text-white rounded-xl p-4 shadow-lg hover:bg-primary-700 transition-colors touch-manipulation"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-primary-100 text-sm">Готов к тренировке?</p>
                <p className="text-lg font-semibold">Начать тренировку</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
                <PlusIcon className="w-6 h-6" />
              </div>
            </div>
          </Link>
        )}

        {/* This Week Stats */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title">Активность</h2>
          </div>
          <div className="card p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {currentWeek ? formatWork(currentWeek.total_work) : '0'}
                </div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide">Объём</div>
              </div>
              <div className="text-center border-l border-gray-100">
                <div className="text-2xl font-bold text-gray-900">
                  {currentWeek?.workouts_count || 0}
                </div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide">Тренировок</div>
              </div>
            </div>
          </div>
        </section>

        {/* Muscle Balance */}
        {currentWeek && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="section-title">Баланс мышц</h2>
            </div>
            <div className="card p-4">
              <div className="grid grid-cols-4 gap-4">
                {MUSCLE_GROUPS.map((muscle) => {
                  const sets = Math.max(0, currentWeek.sets_by_muscle[muscle] || 0);
                  const lastWeekSets = Math.max(0, lastWeek?.sets_by_muscle[muscle] || 0);
                  return (
                    <MuscleRing
                      key={muscle}
                      muscle={muscle}
                      sets={sets}
                      lastWeekSets={lastWeekSets}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
